import { array, func, instanceOf, object, oneOfType, shape, string } from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Radio from 'ravenjs/lib/Radio';
import List from 'ravenjs/lib/List';
import Typography from 'ravenjs/lib/Typography';
import Alert from 'ravenjs/lib/Alert';
import Box from 'ravenjs/lib/Box';
import ListItem from 'ravenjs/lib/ListItem';
import Checkbox from 'ravenjs/lib/CheckboxNew';

import { DEFAULT_COLORS } from 'constants/colors';
import MESSAGES from 'constants/messages';
import { SPACING } from 'constants/spacing';
import { sanitizeMarkup } from 'utils/sanitize';
import { CheckboxEmptyIcon, CheckboxFilledIcon, WarningIcon } from '../Icons';

const QuestionnaireWrapper = styled(Box)`
    overflow-y: auto;
    .check-item {
        margin-right: 0.875rem;
    }
`;

const QuestionnaireContainer = styled.div`
    padding: ${SPACING.XL} 2rem;
`;

const TitleStyled = styled.div`
    display: flex;
`;

const Questionnaire = ({
    currentCategory,
    missedQuestions,
    currentCategoryName,
    selectedAnswers,
    onChangeOption,
    containerRef,
}) => {
    const renderAnswer = (item, questionId, type) => {
        const { id, answer } = item;
        const checked = selectedAnswers[questionId]
            ? selectedAnswers[questionId].indexOf(id) !== -1
            : false;
        const onChange = () => {
            onChangeOption(id, questionId, type);
        };
        const component =
            type === 'Multiple Choice' ? (
                <Radio
                    checked={checked}
                    id={id}
                    className="check-item"
                    name={questionId}
                    onChange={onChange}
                    required
                    value={id}
                />
            ) : (
                <Checkbox
                    checked={checked}
                    id={id}
                    type="checkbox"
                    className="check-item"
                    icon={CheckboxEmptyIcon}
                    checkedIcon={CheckboxFilledIcon}
                    onChange={onChange}
                />
            );

        return (
            <ListItem
                key={id}
                padding="0.375rem 0"
                style={{ display: 'flex', alignItems: 'flex-start' }}
            >
                {component}
                <Typography
                    type="span"
                    gutterTop="0"
                    gutterBottom="0"
                    onClick={onChange}
                    fontSize="1rem"
                    fontFamily="Roboto"
                    style={{
                        cursor: 'pointer',
                    }}
                >
                    {answer}
                </Typography>
            </ListItem>
        );
    };

    const renderMissedAlert = () => {
        return (
            missedQuestions[currentCategoryName] &&
            missedQuestions[currentCategoryName].length > 0 && (
                <Alert
                    backgroundColor={DEFAULT_COLORS.BRAND_RED_2}
                    borderColor={DEFAULT_COLORS.BRAND_RED_2}
                    margin="0"
                    borderRadius="0"
                    style={{
                        position: 'sticky',
                        top: '0',
                        left: '0',
                        zIndex: '2',
                    }}
                >
                    <WarningIcon
                        fill={DEFAULT_COLORS.BLACK}
                        style={{
                            alignSelf: 'center',
                            marginRight: '0.8rem',
                        }}
                    />
                    <Typography gutterBottom="5px">
                        You missed {missedQuestions[currentCategoryName].length} questions.
                    </Typography>
                </Alert>
            )
        );
    };

    const renderIcon = id => {
        return (
            missedQuestions[currentCategoryName].indexOf(id) !== -1 && (
                <Typography type="span" gutterTop="0.3rem" gutterBottom="0" gutterRight={SPACING.M}>
                    <WarningIcon fill={DEFAULT_COLORS.RED} />
                </Typography>
            )
        );
    };

    const renderQuestionsByCategory = () => {
        if (!currentCategory) {
            return null;
        }

        return (
            <>
                {renderMissedAlert()}
                <QuestionnaireContainer ref={containerRef}>
                    {currentCategory.map(({ answers, id, question, type }) => (
                        <React.Fragment key={id}>
                            <TitleStyled>
                                {renderIcon(id)}
                                <Typography type="QuestionnaireQuestion" gutterTop="0">
                                    {question}
                                </Typography>
                            </TitleStyled>
                            <Typography
                                type="bodyNew"
                                dangerouslySetInnerHTML={
                                    type === 'Multiple Choice'
                                        ? sanitizeMarkup(MESSAGES.HR_FITNESS.MULTIPLE_CHOICE)
                                        : sanitizeMarkup(MESSAGES.HR_FITNESS.MULTIPLE_SELECT)
                                }
                            />
                            <List
                                style={{
                                    width: '100%',
                                    marginBottom: '2rem',
                                }}
                            >
                                {answers.map(answer => {
                                    return renderAnswer(answer, id, type);
                                })}
                            </List>
                        </React.Fragment>
                    ))}
                </QuestionnaireContainer>
            </>
        );
    };
    return (
        <QuestionnaireWrapper
            position="relative"
            borderRadius="4px"
            borderColor={DEFAULT_COLORS.DARK}
            backgroundColor={DEFAULT_COLORS.WHITE}
            padding="0"
            margin={`0 0 ${SPACING.XL} 0`}
            height="29.125rem"
        >
            {renderQuestionsByCategory()}
        </QuestionnaireWrapper>
    );
};

Questionnaire.propTypes = {
    currentCategory: array,
    containerRef: oneOfType([func, shape({ current: instanceOf(Element) })]).isRequired,
    missedQuestions: array,
    currentCategoryName: string,
    selectedAnswers: object,
    onChangeOption: func.isRequired,
};

Questionnaire.defaultProps = {
    currentCategory: [],
    missedQuestions: [],
    selectedAnswers: {},
    currentCategoryName: '',
};

export default Questionnaire;
